import type {
  Employee,
  PagedResult,
  Round,
  Ranking,
  ComparisonWithQuestions,
  Comparison,
  RecordAnswerRequest,
  CreateRoundRequest,
  UnknownEmployee,
  RoundEvaluatorDTO,
  AuditLog,
  AuditUser
} from '../types';
import { msalInstance, loginRequest } from '../authConfig';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

/**
 * Resolves a photo URL from the backend (e.g. "/api/employees/5/photo")
 * into a full URL the browser can fetch. Returns null if no photo.
 */
export function resolvePhotoUrl(photoURL: string | null | undefined): string | null {
  if (!photoURL) return null;
  if (photoURL.startsWith('http') || photoURL.startsWith('data:')) return photoURL;
  // Backend returns "/api/employees/{id}/photo" — strip the /api prefix and prepend API_BASE_URL
  if (photoURL.startsWith('/api/')) return API_BASE_URL + photoURL.substring(4);
  return photoURL;
}

// Initialize MSAL and resolve the redirect promise once, so concurrent API calls don't deadlock
const redirectPromise = msalInstance.initialize().then(() => msalInstance.handleRedirectPromise());

function forceRelogin(): never {
  msalInstance.setActiveAccount(null);
  msalInstance.loginRedirect(loginRequest);
  throw new Error('Session expired. Redirecting to login...');
}

// Deduplicate concurrent token requests � only one in-flight at a time
let tokenPromise: Promise<string> | null = null;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Token acquisition timed out')), ms)
    ),
  ]);
}

async function acquireToken(): Promise<string> {
  await redirectPromise;

  let account = msalInstance.getActiveAccount();

  if (!account) {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      account = accounts[0];
      msalInstance.setActiveAccount(account);
    } else {
      forceRelogin();
    }
  }

  try {
    // Timeout after 10s � if silent refresh hangs (Azure AD unreachable), redirect to login
    const response = await withTimeout(
      msalInstance.acquireTokenSilent({ ...loginRequest, account }),
      10000
    );
    return response.accessToken;
  } catch {
    // Silent failed (timeout, expired refresh token, interaction required, etc.)
    // Redirect to login page for a fresh authentication
    forceRelogin();
  }
}

function getAccessToken(): Promise<string> {
  if (!tokenPromise) {
    tokenPromise = acquireToken().finally(() => {
      tokenPromise = null;
    });
  }
  return tokenPromise;
}

async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  const token = await getAccessToken();
  const headers: Record<string, string> = {
    ...((options.headers as Record<string, string>) || {}),
    'Authorization': `Bearer ${token}`,
  };
  const response = await fetch(url, { ...options, headers, cache: 'no-store' });

  if (response.status === 401) {
    forceRelogin();
  }

  return response;
}

/** Throws an error with `deleted: true` if the response indicates the round was deleted. */
async function throwIfRoundDeleted(response: Response): Promise<void> {
  if (response.status === 404) {
    try {
      const body = await response.json();
      if (body?.deleted) {
        const err: any = new Error(body.message || 'This round has been deleted.');
        err.deleted = true;
        throw err;
      }
    } catch (e: any) {
      if (e?.deleted) throw e;
    }
  }
}

// API Service
export const api = {
  async getCurrentUser(): Promise<Employee> {
    const response = await fetchWithAuth(`${API_BASE_URL}/auth/me`);
    if (!response.ok) throw new Error('Failed to fetch current user');
    return response.json();
  },

  // Called once after a genuine interactive sign-in (MSAL LOGIN_SUCCESS event).
  // Not called on page reload or silent token refresh.
  async logLogin(): Promise<void> {
    await fetchWithAuth(`${API_BASE_URL}/auth/login`, { method: 'POST' });
  },

  async logLogout(): Promise<void> {
    await fetchWithAuth(`${API_BASE_URL}/auth/logout`, { method: 'POST' });
  },

  async getEmployees(): Promise<Employee[]> {
    const response = await fetchWithAuth(`${API_BASE_URL}/employees`);
    if (!response.ok) throw new Error('Failed to fetch employees');
    return response.json();
  },

  async getEmployeesPaged(page: number, pageSize: number, search?: string, sortField?: string, sortDirection?: string, manager?: string, excludeIds?: number[]): Promise<PagedResult<Employee>> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });
    if (search) {
      params.set('search', search);
    }
    if (sortField) {
      params.set('sortField', sortField);
      params.set('sortDirection', sortDirection || 'asc');
    }
    if (manager) {
      params.set('manager', manager);
    }
    if (excludeIds && excludeIds.length > 0) {
      params.set('excludeIds', excludeIds.join(','));
    }
    const response = await fetchWithAuth(`${API_BASE_URL}/employees?${params}`);
    if (!response.ok) throw new Error('Failed to fetch employees');
    return response.json();
  },

  async getManagersOf(employeeIds: number[]): Promise<Employee[]> {
    if (employeeIds.length === 0) return [];
    const params = new URLSearchParams({ employeeIds: employeeIds.join(',') });
    const response = await fetchWithAuth(`${API_BASE_URL}/employees/managers-of?${params}`);
    if (!response.ok) throw new Error('Failed to fetch managers');
    return response.json();
  },

  async getEmployeeIds(search?: string, manager?: string): Promise<number[]> {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (manager) params.set('manager', manager);
    const query = params.toString();
    const response = await fetchWithAuth(`${API_BASE_URL}/employees/ids${query ? `?${query}` : ''}`);
    if (!response.ok) throw new Error('Failed to fetch employee IDs');
    return response.json();
  },

  async getEmployeeCount(): Promise<number> {
    const response = await fetchWithAuth(`${API_BASE_URL}/employees/count`);
    if (!response.ok) throw new Error('Failed to fetch employee count');
    return response.json();
  },

  async getDistinctManagers(): Promise<string[]> {
    const response = await fetchWithAuth(`${API_BASE_URL}/employees/managers`);
    if (!response.ok) throw new Error('Failed to fetch managers');
    return response.json();
  },

  async getRounds(): Promise<Round[]> {
    const response = await fetchWithAuth(`${API_BASE_URL}/rounds`);
    if (!response.ok) throw new Error('Failed to fetch rounds');
    return response.json();
  },

  async createRound(data: CreateRoundRequest): Promise<Round> {
    const response = await fetchWithAuth(`${API_BASE_URL}/rounds`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const errorBody = await response.text();
      console.error('Create round failed:', response.status, errorBody);
      throw new Error(`Failed to create round (${response.status}): ${errorBody}`);
    }
    return response.json();
  },

  async updateRoundStatus(id: number, status: string): Promise<Round> {
    const response = await fetchWithAuth(`${API_BASE_URL}/rounds/${id}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status })
    });
    if (!response.ok) throw new Error('Failed to update round status');
    return response.json();
  },

  /** Marks the round completed only if ALL evaluators have finished their assigned comparisons.
   *  Safe to call when any individual evaluator runs out of pairs. */
  async tryCompleteRound(id: number): Promise<{ completed: boolean }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/rounds/${id}/try-complete`, {
      method: 'POST'
    });
    if (!response.ok) throw new Error('Failed to check round completion');
    return response.json();
  },

  async getRankings(roundId: number, evaluatorId?: number): Promise<Ranking[]> {
    const params = evaluatorId ? `?evaluatorId=${evaluatorId}` : '';
    const response = await fetchWithAuth(`${API_BASE_URL}/rounds/${roundId}/rankings${params}`);
    if (!response.ok) throw new Error('Failed to fetch rankings');
    return response.json();
  },

  async getAggregateRankings(roundIds?: number[]): Promise<Ranking[]> {
    const params = roundIds && roundIds.length > 0 ? `?roundIds=${roundIds.join(',')}` : '';
    const response = await fetchWithAuth(`${API_BASE_URL}/rounds/aggregate-rankings${params}`);
    if (!response.ok) throw new Error('Failed to fetch Aggregate rankings');
    return response.json();
  },

  async getNextPair(evaluatorId: number, roundId: number): Promise<ComparisonWithQuestions | null> {
    const response = await fetchWithAuth(`${API_BASE_URL}/comparisons/next?evaluatorId=${evaluatorId}&roundId=${roundId}`);
    if (!response.ok) {
      if (response.status === 404) {
        await throwIfRoundDeleted(response);
        return null;
      }
      if (response.status === 403) {
        const err: any = new Error('You are not assigned as an evaluator for this round');
        err.status = 403;
        throw err;
      }
      throw new Error('Failed to fetch next pair');
    }
    return response.json();
  },

  async recordAnswer(data: RecordAnswerRequest): Promise<ComparisonWithQuestions> {
    const response = await fetchWithAuth(`${API_BASE_URL}/comparisons/answer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      await throwIfRoundDeleted(response);
      throw new Error('Failed to record answer');
    }
    return response.json();
  },

  async submitAndComplete(data: RecordAnswerRequest): Promise<{ message: string }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/comparisons/submit-and-complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      await throwIfRoundDeleted(response);
      throw new Error('Failed to submit comparison');
    }
    return response.json();
  },

  async skipComparison(comparisonId: number, reason: string = 'unknown', unknownEmployeeId?: number): Promise<{ message: string; reason: string }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/comparisons/${comparisonId}/skip`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason, unknownEmployeeId })
    });
    if (!response.ok) {
      await throwIfRoundDeleted(response);
      const errorBody = await response.text();
      throw new Error(`Failed to skip comparison (${response.status}): ${errorBody}`);
    }
    return response.json();
  },

  async getUserComparisons(evaluatorId: number, roundId: number): Promise<Comparison[]> {
    const response = await fetchWithAuth(`${API_BASE_URL}/comparisons/evaluator/${evaluatorId}/round/${roundId}`);
    if (!response.ok) throw new Error('Failed to fetch user comparisons');
    return response.json();
  },

  async getUnknownEmployees(roundId: number): Promise<UnknownEmployee[]> {
    const response = await fetchWithAuth(`${API_BASE_URL}/comparisons/unknown-employees?roundId=${roundId}`);
    if (!response.ok) {
      if (response.status === 404) return [];
      throw new Error('Failed to fetch unknown employees');
    }
    return response.json();
  },

  async syncEntraIDUsers() {
    const response = await fetchWithAuth(`${API_BASE_URL}/employees/sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    if (!response.ok) throw new Error('Failed to sync EntraID users');
    return response.json();
  },

  async syncPhotos() {
    const response = await fetchWithAuth(`${API_BASE_URL}/employees/sync-photos`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    if (!response.ok) throw new Error('Failed to sync photos');
    return response.json();
  },

  async assignEvaluators(roundId: number, evaluatorIds: number[], finalReviewerId?: number): Promise<{ message: string; count: number }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/rounds/${roundId}/evaluators`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ evaluatorIDs: evaluatorIds, finalReviewerId })
    });
    if (!response.ok) throw new Error('Failed to assign evaluators');
    return response.json();
  },

  async getRoundEvaluators(roundId: number): Promise<RoundEvaluatorDTO[]> {
    const response = await fetchWithAuth(`${API_BASE_URL}/rounds/${roundId}/evaluators`);
    if (!response.ok) throw new Error('Failed to fetch round evaluators');
    return response.json();
  },

  async getAssignedRoundIds(evaluatorId: number): Promise<number[]> {
    const response = await fetchWithAuth(`${API_BASE_URL}/rounds/assigned?evaluatorId=${evaluatorId}`);
    if (!response.ok) throw new Error('Failed to fetch assigned round IDs');
    return response.json();
  },

  async getEvaluatorProgress(roundId: number): Promise<import('../types').EvaluatorProgress[]> {
    const response = await fetchWithAuth(`${API_BASE_URL}/rounds/${roundId}/evaluator-progress`);
    if (!response.ok) throw new Error('Failed to fetch evaluator progress');
    return response.json();
  },

  async getChildRounds(parentRoundId: number): Promise<import('../types').Round[]> {
    const response = await fetchWithAuth(`${API_BASE_URL}/rounds/${parentRoundId}/children`);
    if (!response.ok) throw new Error('Failed to fetch child rounds');
    return response.json();
  },

  // Audit Log
  async getAuditLogs(
    page: number,
    pageSize: number,
    actionType?: string,
    search?: string,
    startDate?: string,
    endDate?: string,
    employeeId?: number
  ): Promise<PagedResult<AuditLog>> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });
    if (actionType) params.set('actionType', actionType);
    if (search) params.set('search', search);
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (employeeId) params.set('employeeId', employeeId.toString());
    const response = await fetchWithAuth(`${API_BASE_URL}/auditlog?${params}`);
    if (!response.ok) throw new Error('Failed to fetch audit logs');
    return response.json();
  },

  async getAuditActionTypes(): Promise<string[]> {
    const response = await fetchWithAuth(`${API_BASE_URL}/auditlog/action-types`);
    if (!response.ok) throw new Error('Failed to fetch audit action types');
    return response.json();
  },

  async getAuditUsers(): Promise<AuditUser[]> {
    const response = await fetchWithAuth(`${API_BASE_URL}/auditlog/users`);
    if (!response.ok) throw new Error('Failed to fetch audit users');
    return response.json();
  },

  async logDataExport(description?: string, entityType?: string, entityId?: number): Promise<void> {
    await fetchWithAuth(`${API_BASE_URL}/auditlog/log-export`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ description, entityType, entityId })
    });
  },

  async updateEmployeeRole(employeeId: number, role: 'admin' | 'user'): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/employees/${employeeId}/role`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role })
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to update employee role' }));
      throw new Error(errorData.message || 'Failed to update employee role');
    }
  },

  async getComparisonById(comparisonId: number): Promise<ComparisonWithQuestions | null> {
    const response = await fetchWithAuth(`${API_BASE_URL}/comparisons/${comparisonId}`);
    if (!response.ok) {
      if (response.status === 404) return null;
      throw new Error('Failed to fetch comparison');
    }
    return response.json();
  },

  async undoComparison(comparisonId: number, evaluatorId: number): Promise<{ message: string }> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/comparisons/${comparisonId}/undo?evaluatorId=${evaluatorId}`,
      { method: 'DELETE' }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to undo comparison' }));
      throw new Error(errorData.message || 'Failed to undo comparison');
    }
    return response.json();
  },

  async deleteRound(roundId: number): Promise<{ message: string }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/rounds/${roundId}`, {
      method: 'DELETE'
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to delete round' }));
      throw new Error(errorData.message || 'Failed to delete round');
    }
    return response.json();
  },

  async undoSkip(comparisonId: number, evaluatorId: number): Promise<{ message: string }> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/comparisons/${comparisonId}/undo-skip?evaluatorId=${evaluatorId}`,
      { method: 'DELETE' }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to undo skip' }));
      throw new Error(errorData.message || 'Failed to undo skip');
    }
    return response.json();
  }
};
